import * as store from '@attain-apps/fe/web/main/lib/store'

import type * as ecModels from '../../../models'

import type { Plugin } from './plugin'
import type { State } from './state'

/**
 * Core actions for this {@link Plugin}
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig<
  TTypeParams extends ecModels.TypeParams.Constraint,
>() {
  return {
    /**
     * Applies the {@link Plugin.editedParams} by overriding the
     * {@link Plugin.appliedParams}
     */
    applyParams: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.NONE,
      reducers: {
        success: ({ state }) => ({ ...state, isEditingParams: false }),
      },
    }),
    /**
     * Queries for the count of entities that match the
     * {@link Plugin.appliedParams} and updates the state
     */
    countMatches: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<{ matchedCount: number }>(),
      reducersDeprecated: {
        success: (state, data) => ({
          ...state,
          matchedCount: data.response.matchedCount,
        }),
      },
    }),
    /**
     * Discards any changes made to the {@link Plugin.editedParams}
     */
    discardParams: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.NONE,
      reducers: {
        success: ({ state }) => ({ ...state, isEditingParams: false }),
      },
    }),
    /**
     * Adds items to the list of IDs in the state
     */
    insert: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly ids: readonly ecModels.EntityId[]
      }>(),
      reducerDeprecated: (state: State<TTypeParams>, { ids }) => ({
        ...state,
        ids: [...state.ids, ...ids.filter(id => !state.ids.includes(id))],
        isLoading: false,
      }),
    }),
    /**
     * Queries for the entities that match the {@link Plugin.appliedParams}
     */
    load: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<{
        readonly ids: readonly ecModels.EntityId[]
      }>(),
      reducers: {
        cancellation: ({ state }) => ({
          ...state,
          isLoading: false,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          error,
          isLoading: false,
        }),
        request: ({ state }) => ({
          ...state,
          error: undefined,
          isLoading: true,
        }),
        success: ({ data: { response }, state }) => ({
          ...state,
          ids: response.ids,
          isLoading: false,
        }),
      },
    }),
    /**
     * Removes items from the list of IDs in the state
     */
    remove: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly ids: readonly ecModels.EntityId[]
      }>(),
      reducerDeprecated: (state: State<TTypeParams>, { ids }) => ({
        ...state,
        ids: state.ids.filter(id => !ids.includes(id)),
      }),
    }),
    /**
     * Sets the list of IDs in the state
     */
    set: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly ids: readonly ecModels.EntityId[]
      }>(),
      reducerDeprecated: (state: State<TTypeParams>, { ids }) => ({
        ...state,
        ids,
        isLoading: false,
      }),
    }),
    /**
     * Pre-populates the {@link Plugin.editedParams} with the values from
     * current {@link Plugin.appliedParams}
     */
    startEditingParams: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.NONE,
      reducers: {
        success: ({ state }) => ({ ...state, isEditingParams: true }),
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State<TTypeParams>>
}
